import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { firebaseConfig, publicVapidKey, SW_FILENAME } from "./firebase-config";

interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}
interface ExtendedNotificationOptions extends NotificationOptions {
  actions?: NotificationAction[];
}

export default class FirebaseUtil {
  private static messaging: ReturnType<typeof getMessaging> | null = null;
  private static swRegistration: ServiceWorkerRegistration | null = null;

  /**
   * Initialize Firebase and Messaging
   */
  static async initializeApp(): Promise<boolean> {
    try {
      // Register Service Worker first
      await FirebaseUtil.registerServiceWorker();

      if (await isSupported()) {
        const app = initializeApp(firebaseConfig);
        FirebaseUtil.messaging = getMessaging(app);
        console.log("Firebase initialized successfully.");

        // Request Notification Permission
        const permission = await FirebaseUtil.requestNotificationPermission();
        if (!permission) {
          console.warn("Notification permission not granted.");
          return false;
        }

        // Setup listeners for foreground notifications
        FirebaseUtil.setupListeners();

        return true;
      } else {
        console.warn("Firebase Messaging is not supported in this browser.");
        return false;
      }
    } catch (error) {
      console.error("Error initializing Firebase:", error);
      return false;
    }
  }

  /**
   * Register Service Worker
   */
  private static async registerServiceWorker(): Promise<void> {
    if (!("serviceWorker" in navigator)) {
      console.error("Service Workers are not supported");
      throw new Error("Service Workers are not supported");
    }

    try {
      // First, check if we already have an active service worker
      const existingRegistration =
        await navigator.serviceWorker.getRegistration();

      if (existingRegistration) {
        // If the existing service worker is in a failed state or needs update
        if (existingRegistration.installing || existingRegistration.waiting) {
          await existingRegistration.unregister();
          console.log("Unregistered existing service worker");
        } else if (existingRegistration.active) {
          // If we have an active service worker, check if it's the correct one
          if (
            existingRegistration.scope === "/" &&
            existingRegistration.active.scriptURL.includes(SW_FILENAME)
          ) {
            console.log("Using existing service worker");
            FirebaseUtil.swRegistration = existingRegistration;
            return;
          }
          await existingRegistration.unregister();
        }
      }

      // Register new service worker
      FirebaseUtil.swRegistration = await navigator.serviceWorker.register(
        `/${SW_FILENAME}`,
        {
          scope: "/",
        }
      );

      console.log(
        "Service Worker registered successfully:",
        FirebaseUtil.swRegistration
      );

      // Wait for the service worker to be activated
      if (FirebaseUtil.swRegistration.installing) {
        await new Promise<void>((resolve) => {
          FirebaseUtil.swRegistration?.installing?.addEventListener(
            "statechange",
            (event) => {
              if ((event.target as ServiceWorker).state === "activated") {
                resolve();
              }
            }
          );
        });
      }

      console.log("Service Worker is active");
    } catch (error) {
      console.error("Service Worker registration failed:", error);
      throw error;
    }
  }

  /**
   * Request Notification Permission
   * @returns true if permission is granted, false otherwise
   */
  private static async requestNotificationPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        console.log("Notification permission granted.");
        return true;
      } else {
        console.warn("Notification permission denied.");
        return false;
      }
    } catch (error) {
      console.error("Error during notification permission request:", error);
      return false;
    }
  }

  /**
   * Get Firebase Cloud Messaging Token
   * @returns the token string or null if error
   */
  static async getToken(): Promise<string | null> {
    try {
      if (!FirebaseUtil.messaging) {
        throw new Error("Firebase Messaging is not initialized.");
      }
      const token = await getToken(FirebaseUtil.messaging, {
        vapidKey: publicVapidKey,
        serviceWorkerRegistration: FirebaseUtil.swRegistration,
      });
      return token;
    } catch (error) {
      console.error("Error getting FCM token:", error);
      return null;
    }
  }

  private static setupListeners(): void {
    if (!FirebaseUtil.messaging) {
      throw new Error("Firebase Messaging is not initialized.");
    }

    // Foreground Notifications - when app is in the foreground
    onMessage(FirebaseUtil.messaging, (payload) => {
      console.log("Foreground notification received:", payload);

      if (firebaseConfig.allowForegroundNotifications) {
        // Create a new notification options object
        const notificationOptions: NotificationOptions = {
          body: payload.notification?.body || "You have a new message.",
          icon: "/images/app_icon.png",
          badge: "/badge-icon.png", // Optional
          data: payload.data,
          requireInteraction: true, // Keep the notification until user interacts
          tag: "foreground-notification", // Optional: for notification grouping
        };

        try {
          // First try using the Notifications API directly
          if (Notification.permission !== "granted") {
            return;
          }
          // Fallback to service worker if direct notification fails
          if (FirebaseUtil.swRegistration) {
            FirebaseUtil.swRegistration.showNotification(
              payload.notification?.title || "New Notification",
              notificationOptions
            );
          } else {
            console.error(
              "Neither direct notifications nor service worker available"
            );
          }
        } catch (error) {
          console.error("Error showing foreground notification:", error);
        }
      } else {
        console.log("Foreground notifications are disabled in config.");
      }
    });
  }

  /**
   * Utility to show notifications with sound
   * @param title Notification title
   * @param body Notification body text
   */
  private static async showNotification(
    title: string,
    body: string
  ): Promise<void> {
    if (Notification.permission !== "granted") {
      console.warn("Notification permission not granted");
      return;
    }

    try {
      const notificationOptions: NotificationOptions = {
        body,
        icon: "/firebase-logo.png",
        requireInteraction: true,
        silent: false, // Ensure sound is enabled
      };

      // Try direct notification first
      if (document.visibilityState === "visible") {
        new Notification(title, notificationOptions);
      }
      // Use service worker for background notifications
      else if (FirebaseUtil.swRegistration) {
        await FirebaseUtil.swRegistration.showNotification(
          title,
          notificationOptions
        );
      }
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  }

  /**
   * Check if browser supports notifications
   */
  private static async checkNotificationSupport(): Promise<boolean> {
    if (!("Notification" in window)) {
      console.error("This browser does not support notifications");
      return false;
    }
    return true;
  }

  static async showLocalNotification(
    title: string,
    body: string,
    options: {
      showOnlyInBackgroundmode?: boolean;
      autoClear?: boolean;
      duration?: number;
      requireInteraction?: boolean;
      tag?: string;
      showTimeOut?: boolean;
      timeOutTitle?: string;
      timeOutBody?: string;
      actions?: {}[];
    } = {}
  ): Promise<string> {
    try {
      const {
        showOnlyInBackgroundmode = false,
        autoClear = false,
        duration = 5000,
        requireInteraction = false,
        tag = "timed-notification",
        showTimeOut = false,
        timeOutTitle = "Notification",
        timeOutBody = "You have a new notification",
        actions = [],
      } = options;

      if (showOnlyInBackgroundmode && !document.hidden) {
        console.log("Document is visible, not showing notification.");
        return "Notification not shown: Document is visible.";
      }

      // Check if notifications are supported
      if (!(await FirebaseUtil.checkNotificationSupport())) {
        return "Notifications are not supported on this browser.";
      }

      if (!("serviceWorker" in navigator)) {
        console.error("Service Worker not supported.");
        return "Service Worker not supported.";
      }

      const registration = await navigator.serviceWorker.ready;
      if (!registration) {
        console.error("Service Worker not registered.");
        return "Service Worker not registered.";
      }
      // Explicitly type the array and use Array.from to ensure it's an array
      const buttons = actions.map(button => ({
        action: button,
        title: button
      })) as NotificationAction[];

      const notificationOptions: ExtendedNotificationOptions = {
        body: body,
        icon: "/images/app_icon.png",
        silent: false,
        requireInteraction: requireInteraction,
        tag: tag,
        actions: buttons
      };

      // Show the notification
      await registration.showNotification(title, notificationOptions);

      console.log("Notification shown:", title);

      // Auto-clear the notification if enabled
      if (autoClear) {
        setTimeout(async () => {
          const notifications = await registration.getNotifications({
            tag: tag,
          });
          notifications.forEach((notification) => notification.close());
          console.log(`Auto-cleared notification with tag: ${tag}`);
        }, duration);
      }

      // Show timeout notification if enabled
      if (showTimeOut) {
        setTimeout(async () => {
          const notifications = await registration.getNotifications({
            tag: tag,
          });
          if (notifications.length === 0) {
            return;
          }
          notifications.forEach((notification) => notification.close());
          console.log(`Cleared timeout notification with tag: ${tag}`);

          await registration.showNotification(timeOutTitle, {
            body: timeOutBody,
            icon: "/images/app_icon.png",
            silent: true,
            requireInteraction: false,
            tag: tag,
          });

          console.log(`Timeout notification shown: ${timeOutTitle}`);
        }, duration);
      }

      return `Notification '${title}' displayed successfully.`;
    } catch (error) {
      console.error("Error showing notification:", error);
      return `Error showing notification: ${error}`;
    }
  }
  static async clearNotificationsWithTag(
    params: { tag?: string } = { tag: "timed-notification" }
  ): Promise<string> {
    try {
      const tag = params.tag ?? "timed-notification";

      if (!("serviceWorker" in navigator)) {
        console.error("Service Worker not supported.");
        return "Service Worker not supported.";
      }

      const registration = await navigator.serviceWorker.ready;
      if (!registration) {
        console.error("Service Worker not registered.");
        return "Service Worker not registered.";
      }

      // Get all notifications with the given tag
      const notifications = await registration.getNotifications({
        tag: tag,
      });

      notifications.forEach((notification) => {
        notification.close();
      });

      console.log("Cleared notifications with tag:", tag);
      return `Notifications with tag '${tag}' cleared successfully.`;
    } catch (error) {
      console.error("Error clearing notifications:", error);
      return `Error clearing notifications: ${error}`;
    }
  }
}
